import { Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { View, Feature } from 'ol';
// import GeometryType from 'ol/geom/GeometryType';
import { getPointResolution } from 'ol/proj';
import { FeatureStyle } from '../models/style.model';
import { ConfigSidebarService } from './config-sidebar.service';
import { IStylesConfig } from '../interfaces/style.interface';
import { IDrawingToolOptions } from '../models/drawingToolOptions.model';
import { MapService } from './map.service';

/** Options of geometries that can be drawn using the drawing tool */
export enum DrawingGeometries {
  POINT = 'point',
  LINE = 'line',
  POLYGON = 'polygon',
  CIRCLE = 'circle',
  TEXT = 'text'
}

/**
 * Helper service for the Drawing Tool
 * This service helps communicate the drawing tool with other components and contains some utility functions that are used
 * inside the Drawing Tool.
 */
@Injectable({
  providedIn: 'root'
})
export class DrawingService {
  /**
   * GLobal variable that indicates if the Drawing Tool is open.
   */
  drawingToolOpen: boolean = false;

  drawingToolOptions: IDrawingToolOptions;

  /**
   * Emitted every time the drawing tool is closed, with or without a geometry.
   * Used by whoever opened the tool as a sub-step of its own flow (e.g. DXF export) to resume.
   */
  drawingToolClosed$: Subject<void> = new Subject<void>();

  /**
   * Event that indicates current geometry.
   */
  drawingGeometry$: Subject<Feature> = new Subject<Feature>();

  config: IStylesConfig = null;

  blueFillStyle: FeatureStyle;
  orangeFillStyle: FeatureStyle;
  highlightFeatureFillStyle: FeatureStyle;

  constructor(private configSidebarService: ConfigSidebarService, private mapService: MapService) {}

  private static readonly CLICK_OWNER_ID = 'drawing-tool';

  /**
   * Open the drawing tool component.
   * Il tool è "strong" per il click sulla mappa: se c'è già uno strumento aperto non si apre (il registro mostra l'errore).
   * @returns true se il tool è stato aperto (o era già aperto)
   */
  openDrawingTool(options?: IDrawingToolOptions): boolean {
    if (!this.mapService.acquireMapClick({ id: DrawingService.CLICK_OWNER_ID, level: 'strong' })) {
      return false;
    }
    if (!this.config) {
      this.config = this.configSidebarService.getStyleConfiguration();
    }
    if (options) {
      this.drawingToolOptions = options;
    } else {
      this.drawingToolOptions = {
        point: true,
        line: true,
        polygon: true,
        circle: true,
        text: true
      };
    }

    this.drawingToolOpen = true;
    return true;
  }

  /**
   * Close the drawing tool component.
   */
  closeDrawingTool() {
    this.drawingToolOptions = {
      point: true,
      line: true,
      polygon: true,
      circle: true,
      text: true
    };

    this.drawingToolOpen = false;
    this.mapService.releaseMapClick(DrawingService.CLICK_OWNER_ID);
    this.drawingToolClosed$.next();
  }

  /**
   * Function for the tranformation of the measurement units of the radius (EPSG:3857) to meters
   * @param radius Radius to convert
   * @param viewMappa View of the map object
   * @returns Radius in meters
   */
  convertRadiusToMeters(radius: number, mapView: View): number {
    //TODO CHECK IF THE CONVERSION IS REALLY ACCURATE!
    const equatorResolution = mapView.getResolution();
    const centerResolution = getPointResolution('EPSG:3857', equatorResolution, mapView.getCenter());
    const metersPerUnits = mapView.getProjection().getMetersPerUnit();

    const rad = metersPerUnits * (centerResolution / equatorResolution) * radius;
    // var meters = Math.round(rad); /// 1000 per km;
    const meters = parseFloat(rad.toFixed(4));
    return meters;
  }

  /**
   * Function for the tranformation of the measurement units of the radius from EPSG:3857 units to meters
   * @param meters Meters to convert of the circle radius
   * @param viewMappa Map View
   * @returns Radius in EPSG:3857
   */
  convertMetersToRadius(meters: number, mapView: View): number {
    const equatorResolution = mapView.getResolution();
    const centerResolution = getPointResolution('EPSG:3857', equatorResolution, mapView.getCenter());
    const metersPerUnits = mapView.getProjection().getMetersPerUnit();

    const rad = meters; // * 1000 per km
    const radius = (rad / metersPerUnits) * (equatorResolution / centerResolution);
    return radius;
  }

  /**
   * Get the OpenLayers GeometryType from the custom made DrawingGeometries enumerator.
   * @param drawingGeometry The DrawingGeometries type of a feature.
   * @returns The corresponding GeometryType.
   */
  getGeometryTypeFromDrawingGeometry(drawingGeometry: DrawingGeometries): string {
    if (drawingGeometry == DrawingGeometries.POINT) return 'Point';
    if (drawingGeometry == DrawingGeometries.LINE) return 'LineString';
    if (drawingGeometry == DrawingGeometries.POLYGON) return 'Polygon';
    if (drawingGeometry == DrawingGeometries.CIRCLE) return 'Circle';
    if (drawingGeometry == DrawingGeometries.TEXT) return 'Point';
    return null;
  }

  /**
   * getter for the blue fill feature style
   * @returns {FeatureStyle}
   */
  blueFillFeatureStyle(): FeatureStyle {
    if (!this.blueFillStyle) {
      this.blueFillStyle = this.mapService.mapConfigurationLoaded$
        .getValue()
        .styles.find((el) => el.name == 'BlueFill');
    }
    return this.blueFillStyle;
  }

  /**
   * getter for the orange fill feature style
   * @returns {FeatureStyle}
   */
  orangeFillFeatureStyle(): FeatureStyle {
    if (!this.orangeFillStyle) {
      this.orangeFillStyle = this.mapService.mapConfigurationLoaded$
        .getValue()
        .styles.find((el) => el.name == 'OrangeFill');
    }
    return this.orangeFillStyle;
  }

  /**
   * getter for the highlight feature style
   * @returns {FeatureStyle}
   */
  highlightFeatureStyle(): FeatureStyle {
    if (!this.highlightFeatureFillStyle) {
      this.highlightFeatureFillStyle = this.mapService.mapConfigurationLoaded$
        .getValue()
        .styles.find((el) => el.name == 'HighlightFeatureFill');
    }
    return this.highlightFeatureFillStyle;
  }

  /**
   * getter for the selected feature color
   * @returns {string}
   */
  selectedFeatureColor(): string {
    return this.config.drawingTool.colors.selectedFeature;
  }

  /**
   * getter for the circle center color
   * @returns {string}
   */
  circleCenterColor(): string {
    return this.config.drawingTool.colors.circleCenter;
  }

  /**
   * getter for the modify color
   * @returns {string}
   */
  modifyColor(): string {
    return this.config.drawingTool.colors.modify;
  }

  /**
   * getter for the default text color
   * @returns {string}
   */
  defaultTextColor(): string {
    return this.config.drawingTool.colors.defaultText;
  }
}
